import { Grid, Paper, ToggleButton } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import SearchField from 'src/shared/components/form/searchField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { FormFieldset } from 'src/shared/components/form/formFieldset';
import { StyledToggleButtonGroup } from 'src/shared/components/organisms/styledToggleButtonGroup';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { Box } from '@mui/system';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { SwishConsentSearchFilter } from '../models/swishConsentSearchFilter';
import { SwishConsentDataGrid } from '../components/swishConsentDataGrid';
import { SwishConsentStatuses, SWISH_CONSENT_STATUSES } from '../components/swishConsentStatuses';
import { SwishTabNavigation } from './swishTabNavigation';

// Medgivanden (Swish Återkommande) – både medlemmar och givare
export const SwishConsentOverviewPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);

  // Standardurval: samtliga 8 consent-statusar markerade.
  const defaultStatuses = useMemo(() => SWISH_CONSENT_STATUSES, []);

  const [swishConsentFilter, setSwishConsentFilter] = useLocalStorage(LocalStorageKeys.SwishConsentFilter, {
    // OBS: måste vara ett FÄLT som finns som sorterbar kolumn i griden, annars hamnar MUI DataGrid i en
    // oändlig onSortModelChange-loop (swishConsent_id är ingen kolumn).
    sortCol: 'createdAt',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
    status: defaultStatuses,
  } as SwishConsentSearchFilter);

  const searchFilter: SwishConsentSearchFilter = useMemo(() => {
    return {
      filter: swishConsentFilter?.filter,
      pageSize: swishConsentFilter?.pageSize,
      pageIndex: swishConsentFilter?.pageIndex,
      sortCol: swishConsentFilter?.sortCol,
      sortDir: swishConsentFilter?.sortDir,
      status: swishConsentFilter?.status,
      linkedFilter: swishConsentFilter?.linkedFilter,
    } as SwishConsentSearchFilter;
  }, [
    swishConsentFilter?.filter,
    swishConsentFilter?.pageIndex,
    swishConsentFilter?.pageSize,
    swishConsentFilter?.sortCol,
    swishConsentFilter?.sortDir,
    swishConsentFilter?.status,
    swishConsentFilter?.linkedFilter,
  ]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setSwishConsentFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setSwishConsentFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setSwishConsentFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setSwishConsentFilter, tempSearchString]);

  return (
    <GridWrapper>
      <Grid
        item
        xs={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <MainContentHeader title={t('invoice:swishStatus.header')} />
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <SwishTabNavigation />
          <PageFilterSection
            leftContent={
              <SearchField
                initialValue={searchFilter.filter || ''}
                onSearch={(newSearchString) => {
                  setSearchInitialized(true);
                  setTempSearchString(newSearchString);
                }}
              />
            }
            centerContent={
              <SwishConsentStatuses
                initialValue={swishConsentFilter?.status || defaultStatuses}
                callback={(statuses) => {
                  setSwishConsentFilter((prev) => ({
                    ...prev,
                    status: statuses,
                    pageIndex: 0,
                  }));
                }}
              />
            }
            rightContent={
              <FormFieldset legend={t('invoice:swishStatus.consents.linked')} noPadding displayInline>
                <StyledToggleButtonGroup
                  value={swishConsentFilter?.linkedFilter || 'all'}
                  exclusive
                  onChange={(_e, value) => {
                    if (value !== null) {
                      setSwishConsentFilter((prev) => ({
                        ...prev,
                        linkedFilter: value === 'all' ? undefined : value,
                        pageIndex: 0,
                      }));
                    }
                  }}
                  color='primary'
                  size='small'
                >
                  <ToggleButton value='all'>{t('common:button.all')}</ToggleButton>
                  <ToggleButton value='linked'>{t('invoice:swishStatus.consents.linkedOnly')}</ToggleButton>
                  <ToggleButton value='unlinked'>{t('invoice:swishStatus.consents.unlinkedOnly')}</ToggleButton>
                </StyledToggleButtonGroup>
              </FormFieldset>
            }
          />
          <Box className='section-inset'>
            <SwishConsentDataGrid
              searchFilter={searchFilter}
              searchFilterCallback={(newFilter) =>
                setSwishConsentFilter((prev) => ({
                  ...prev,
                  ...newFilter,
                }))
              }
            />
          </Box>
        </Paper>
      </Grid>
    </GridWrapper>
  );
};
